class CountdownTimer extends HTMLElement {
  static get observedAttributes() {
    return ['end-date'];
  }

  connectedCallback() {
    this._renderUnits();
    this._startTick();
  }

  disconnectedCallback() {
    this._stopTick();
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue === newValue) return;
    requestAnimationFrame(() => {
      this._stopTick();
      this._renderUnits();
      this._startTick();
    });
  }

  _parseEndDate() {
    const raw = this.getAttribute('end-date');
    if (!raw) return null;

    const match = raw.match(
      /^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/
    );
    if (!match) return null;

    const [, dd, mm, yyyy, hh, min, ss] = match;
    return new Date(+yyyy, +mm - 1, +dd, +hh, +min, +ss);
  }

  _getRemainingTime() {
    const endDate = this._parseEndDate();
    const remainingMs = endDate ? Math.max(0, endDate.getTime() - Date.now()) : 0;
    const totalSeconds = Math.floor(remainingMs / 1000);

    return {
      days:    Math.floor(totalSeconds / 86400),
      hours:   Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
      expired: remainingMs === 0,
    };
  }

  _pad(n) {
    return String(n).padStart(2, '0');
  }

  _renderUnits() {
    const { days, hours, minutes, seconds } = this._getRemainingTime();

    this.innerHTML = `
      <div class="countdown-timer__units">
        <div class="countdown-timer__unit">
          <div class="countdown-timer__box" data-unit="days">${this._pad(days)}</div>
          <span class="countdown-timer__label">DAYS</span>
        </div>
        <div class="countdown-timer__unit">
          <div class="countdown-timer__box" data-unit="hours">${this._pad(hours)}</div>
          <span class="countdown-timer__label">HOURS</span>
        </div>
        <div class="countdown-timer__unit">
          <div class="countdown-timer__box" data-unit="minutes">${this._pad(minutes)}</div>
          <span class="countdown-timer__label">MINUTES</span>
        </div>
        <div class="countdown-timer__unit">
          <div class="countdown-timer__box" data-unit="seconds">${this._pad(seconds)}</div>
          <span class="countdown-timer__label">SECONDS</span>
        </div>
      </div>
    `;
  }

  _updateDisplayedValues(days, hours, minutes, seconds) {
    const unitValues = { days, hours, minutes, seconds };

    for (const [unit, value] of Object.entries(unitValues)) {
      const box = this.querySelector(`[data-unit="${unit}"]`);
      const formatted = this._pad(value);
      if (box && box.textContent !== formatted) box.textContent = formatted;
    }
  }

  _startTick() {
    this._stopTick();

    const endDate = this._parseEndDate();
    if (!endDate) return;

    this._tickInterval = setInterval(() => {
      const { days, hours, minutes, seconds, expired } = this._getRemainingTime();

      this._updateDisplayedValues(days, hours, minutes, seconds);

      if (expired) {
        this._stopTick();
        this.dispatchEvent(new CustomEvent('countdown:expired', { bubbles: true, composed: true }));
      }
    }, 1000);
  }

  _stopTick() {
    clearInterval(this._tickInterval);
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
